<template>
  <div class="space-y-3">
    <div class="grid grid-cols-1 md:flex md:justify-between items-center gap-4">
      <!-- <h2 class="text-xl font-semibold text-gray-700 dark:text-gray-200">Daftar Order</h2> -->
      <div class="grid grid-cols-2 gap-4">
        <Button v-if="false" label="Bill" outlined severity="info" @click="$emit('print', 'bill-print')" />
      </div>
    </div>
    <div v-for="item in sortedOrderItems" :key="item.id" :class="[
      'relative transform transition-all duration-200 hover:shadow-lg mr-2 rounded-xl px-2 py-1 border',
      item.payment_status == 2
        ? 'bg-gray-200 dark:bg-dark-800 border-gray-200 dark:border-dark-700'
        : 'bg-blue-50/80 dark:bg-blue-900/20 border-blue-100 dark:border-blue-800/60',
    ]">
      <!-- Product Info -->
      <div class="flex flex-col gap-3">
        <!-- Header Section -->
        <div class="flex flex-col gap-2">
          <!-- Status Badges -->
          <div class="flex flex-wrap items-center gap-2">
            <div v-if="item.status === 'pending'"
              class="text-xs px-2.5 py-1 rounded-full bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-100 font-medium">
              {{
                item.createdAt
                  ? "Order " + formatTime(item.createdAt, "time")
                  : "Sudah dipesan"
              }}
            </div>
            <div v-if="item.status === 'canceled'"
              class="text-xs px-2.5 py-1 rounded-full bg-red-100 text-red-800 dark:bg-red-900 dark:text-red-100 font-medium">
              Cancel {{ item.createdAt ? formatTime(item.createdAt, "time") : "" }}
            </div>
            <div v-else-if="item.status === 'delivered'"
              class="text-xs px-2.5 py-1 rounded-full bg-red-100 text-red-800 dark:bg-red-900 dark:text-red-100 font-medium">
              Sudah diantar
            </div>
            <div v-if="item.status === 'unsaved'"
              class="text-xs px-2.5 py-1 rounded-full bg-yellow-100 text-yellow-800 dark:bg-yellow-900 dark:text-yellow-100 font-medium">
              Belum dipesan
            </div>
          </div>

          <!-- Product Name -->
          <h4 class="font-semibold text-gray-900 dark:text-gray-100 text-base leading-tight">
            {{ item.name }}
          </h4>
        </div>

        <!-- Price Info -->
        <div class="flex flex-col items-end gap-1 min-w-[120px]">
          <!-- Unit Price -->
          <div class="flex items-center gap-2">
            <p v-if="item.discount_price > 0"
              class="font-medium text-gray-400 dark:text-gray-500 line-through text-xs">
              {{ formatRupiah(item.price) }}
            </p>
            <p class="font-semibold text-gray-700 dark:text-gray-200 text-sm">
              {{
                formatRupiah(
                  item.discount_price > 0
                    ? parseFloat(item.price) - parseFloat(item.discount_price)
                    : item.price
                )
              }}
            </p>
          </div>

          <!-- Quantity Controls -->
          <div v-if="item.payment_status == 1" class="flex items-center justify-end mt-1">
            <div class="flex items-center bg-gray-200 dark:bg-dark-800 rounded-lg px-1 py-0.5">
              <button v-if="item.isEdit" @click="
                item.status === 'canceled'
                  ? $emit('reduce-qty-canceled', item)
                  : $emit('reduce-qty', item)
                " :id="'reduce-qty-' + item.id" class="p-1 text-gray-600 dark:text-gray-300 hover:text-blue-500 dark:hover:text-blue-400 transition-colors">
                <MinusCircleIcon class="h-5 w-5" />
              </button>
              <input v-model="item.quantity" type="text"
                class="w-12 text-center bg-transparent font-bold text-gray-900 dark:text-gray-100 text-sm"
                :class="{ 'cursor-not-allowed': !item.isEdit }" :disabled="!item.isEdit"
                @input="updateSubtotalPerItem(item)" oninput="this.value = this.value.replace(/\D/g, '')" />
              <button v-if="item.isEdit" @click="
                item.status === 'canceled'
                  ? $emit('add-qty-canceled', item)
                  : $emit('add-qty', item)
                " :id="'add-qty-' + item.id" class="p-1 text-gray-600 dark:text-gray-300 hover:text-blue-500 dark:hover:text-blue-400 transition-colors">
                <PlusCircleIcon class="h-5 w-5" />
              </button>
            </div>
          </div>

          <!-- Total Price -->
          <div class="flex items-center gap-2">
            <span class="text-xs text-gray-500 dark:text-gray-400 font-medium">Total:</span>
            <p v-if="item.discount_price > 0"
              class="font-medium text-gray-400 dark:text-gray-500 line-through text-sm">
              {{ formatRupiah(item.totalBeforeDiscount) }}
            </p>
            <p class="font-bold text-primary dark:text-primary-300 text-base">
              {{ item.status === "canceled" ? "-" : "" }}{{ formatRupiah(item.totalAfterDiscount) }}
            </p>
          </div>
        </div>

        <!-- Product Mix -->
        <div v-if="item.has_product_mix" class="mt-1">
          <div class="text-xs text-success dark:text-success-300 font-medium">
            <a v-if="item.isEdit" href="#" @click.prevent="$emit('edit-item-mix', item)" 
              class="hover:underline">
              {{ item?.selected_product_mix?.map(mix => `${mix.name} (${mix.quantity})`).join(', ') }}
            </a>
            <template v-else>
              {{ item?.product_mix?.map(mix => `${mix.name} (${mix.quantity})`).join(', ') }}
            </template>
          </div>
        </div>

        <!-- Note Section -->
        <div v-if="item.isEdit || item.note" class="mt-1">
          <!-- Note Input when editing -->
          <div v-if="item.isEdit" class="w-full">
            <input v-model="item.note" type="text" :placeholder="'Tambah catatan untuk ' + item.name"
              class="w-full px-3 py-2 bg-gray-50 dark:bg-dark-900 border border-gray-200 dark:border-dark-600 rounded-lg text-sm focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all dark:text-white placeholder:text-gray-400 dark:placeholder:text-gray-500"
              @change="$emit('update-note', item)" />
          </div>

          <!-- Note Display when not editing -->
          <div v-else-if="item.note" class="text-sm">
            <p class="text-gray-600 dark:text-gray-400">
              <span class="font-medium text-gray-700 dark:text-gray-300">Catatan:</span>
              <span class="ml-1 text-gray-700 dark:text-gray-300">{{ item.note }}</span>
            </p>
          </div>
        </div>
      </div>
      <!-- Delete Button -->
      <button v-if="item.isEdit && item.payment_status == 1" @click="$emit('remove-item', item)"
        :id="'remove-item-' + item.id"
        class="absolute -top-4 -right-4 p-1.5 text-danger hover:text-hover-danger hover:bg-red-50 dark:hover:bg-red-900/50 rounded-lg transition-colors">
        <TrashIcon class="h-5 w-5" />
      </button>
      <button v-if="!item.isEdit && item.status != 'canceled'" label="Cancel" severity="danger"
        @click="$emit('cancel-order', item)"
        class="absolute -top-4 -right-4 p-1.5 text-danger hover:text-hover-danger hover:bg-red-50 dark:hover:bg-red-900/50 rounded-lg transition-colors">
        <TrashIcon class="h-5 w-5" />
      </button>
    </div>
  </div>
</template>

<script>
import {
  MinusCircleIcon,
  PlusCircleIcon,
  TrashIcon,
} from "@heroicons/vue/24/solid";
import { formatRupiah } from "@/helpers/formatRupiah";
import { formatTime } from "@/helpers/formatTime";
import { Button } from "primevue";
import { mapGetters } from "vuex";

export default {
  name: "ListOrdered",
  components: {
    MinusCircleIcon, PlusCircleIcon, TrashIcon, Button,
  },
  emits: [
    "add-qty",
    "add-qty-canceled",
    "reduce-qty",
    "update-subtotal",
    "update-note",
    "remove-item",
    "edit-note",
    "save-note",
    "cancel-order",
    "select-item-mix",
    "delete-item-mix",
  ],
  props: {
    order_items: {
      type: Array,
      required: true,
    },
  },
  computed: {
    ...mapGetters("kitchen_divisions", ["allKitchenDivisions"]),
    sortedOrderItems() {
      return [...this.order_items].sort((a, b) => b.id - a.id);
    },
  },
  async created() {
    await Promise.all([
      this.$store.dispatch("kitchen_divisions/fetchKitchenDivisions", {
        limit: 1000,
      }),
    ]);
    console.log(this.order_items);
  },
  data() {
    return {};
  },
  methods: {
    formatTime,
    formatRupiah,
    isSelectedItemMix(item) {
      console.log(item);
      // return this.order_items?.selected_product_mix.some(mix => mix.id === item.id);
    },
    confirmPrint(type, item) {
      this.$confirm.require({
        group: 'headless',
        header: `Apakah anda yakin ingin mencetak ${type === 'kitchen' ? 'kitchen' : 'checker'}?`,
        message: 'Silahkan konfirmasi sebelum diproses.',
        accept: () => {
          if (type === 'kitchen') {
            this.$emit('printkitchen', item);
          } else {
            this.$emit('printchecker', { items: [item], isTransaction: false });
          }
          this.$toast.add({
            severity: 'success',
            summary: 'Sukses',
            detail: `Berhasil mencetak ${type === 'kitchen' ? 'kitchen' : 'checker'}`,
            life: 3000
          });
        },
        reject: () => {
          this.$toast.add({
            severity: 'error',
            summary: 'Dibatalkan',
            detail: 'Kamu telah membatalkan pencetakan',
            life: 3000
          });
        }
      });
    },
    updateSubtotalPerItem(item) {
      this.$emit("update-subtotal", item);
    },
  },
};
</script>
